/**
 * Renders the generated placement entry points into their anchor divs, each
 * opening the shared chat on click: the button (`bhai-<id>-nav`, stored as nav_launcher) and the
 * IN-PAGE BAR, whose three div names — `bhai-<id>-hero`, `-line` and `-cta` —
 * are aliases of one bar (IN_PAGE_BAR_KEYS in widget-design/schema.ts). They
 * were three placements that drew the same search stack; the names stay
 * because they are on live pages. `-cta` keeps the soft closing band it always
 * had; `data-bhai-band="on"|"off"` sets the band on any of them. Plain DOM. Colors are CSS variables
 * (var(--bhjs-*)) set by applyTheme into a LOW-PRIORITY @layer, so a host
 * designer's CSS can override them and NO theme value is ever string-
 * interpolated into markup. LLM-authored copy is HTML-escaped.
 */
import type { WidgetRuntimeDesign, PlacementRuntime } from "@bluehat/shared/widget-design/runtime";
import { barRadius } from "@bluehat/shared/widget-design/theme-map";
import { askAboutCopy } from "./page-topic.ts";
import { MAX_PAGE_PILLS, pillLabel } from "./page-pills.ts";
import { isJunkSectionHeading, titleishHeading } from "./text-continuity.ts";

export type PlacementKind =
  | "nav_launcher"
  | "hero_bar"
  | "in_content_line"
  | "end_of_page_banner";

/** Placement kind → the `div.bhai-<customerId>-<slot>` suffix the host page
 *  mounts. Exported because the dashboard's design preview has to CREATE one of
 *  these anchors to render a placement, and a second copy of this map in the
 *  dashboard is exactly the duplicated-vocabulary defect CLAUDE.md forbids —
 *  a renamed slot would then silently preview an empty box. */
export const SLOT: Record<PlacementKind, string> = {
  nav_launcher: "nav",
  hero_bar: "hero",
  in_content_line: "line",
  end_of_page_banner: "cta",
};

type Theme = Record<string, string>;

// The generated theme is injected as a LOW-PRIORITY cascade layer, not inline on
// <html>. Inline styles win the cascade — a designer would need !important on
// every var to override — whereas a layered :root rule loses to ANY unlayered
// rule, so the injected colors become DEFAULTS a web designer can override with
// plain CSS (prefers-color-scheme media queries, per-page rules, etc.). Values
// are set via the CSSOM (never string-concatenated → no injection surface), and
// the sheet is adopted on `document` (not in the routed subtree) so it survives
// SPA navigation. Falls back to inline-on-<html> where adoptedStyleSheets /
// @layer aren't supported (functional, just not designer-overridable there).
let themeSheet: CSSStyleSheet | null = null;

function themeRule(): CSSStyleRule | null {
  if (!themeSheet) {
    themeSheet = new CSSStyleSheet();
    themeSheet.replaceSync("@layer bhai-theme {}");
    const layer = themeSheet.cssRules[0];
    if (!(layer instanceof CSSGroupingRule)) return null;
    layer.insertRule(":root {}", 0);
  }
  // Self-heal: re-adopt if an SPA framework replaced document.adoptedStyleSheets.
  if (!document.adoptedStyleSheets.includes(themeSheet)) {
    document.adoptedStyleSheets = [...document.adoptedStyleSheets, themeSheet];
  }
  const layer = themeSheet.cssRules[0];
  const rule = layer instanceof CSSGroupingRule ? layer.cssRules[0] : null;
  return rule instanceof CSSStyleRule ? rule : null;
}

/** Inject the generated theme's --bhjs-* vars. Idempotent + self-healing across
 *  SPA navigation; overridable by host CSS (see the note above). */
export function applyTheme(theme: Theme): void {
  let rule: CSSStyleRule | null = null;
  try {
    if ("adoptedStyleSheets" in Document.prototype && typeof CSSStyleSheet === "function") {
      rule = themeRule();
    }
  } catch {
    rule = null;
  }
  const target = rule ? rule.style : document.documentElement.style; // fallback: inline on <html>
  for (const [suffix, value] of Object.entries(theme)) {
    target.setProperty(`--bhjs-${suffix}`, value);
  }
}

export type PlacementSectionPool = {
  heading: string;
  questions: string[];
};

export type PlacementRenderOptions = {
  /** PR-B: hero monologue lines (opt-in via data-bhai-hero-monologue="on" on the hero anchor). DEFAULT OFF. */
  heroMonologue?: string[];
  /**
   * Text continuity summary (cold page / engaged thread). Applied to the top
   * line and, when engaged, all conversion in-content lines.
   */
  heroSummaryLines?: string[];
  onHeroMonologueShown?: () => void;
  onHeroMonologueEngaged?: () => void;
  /**
   * Corpus page-voice questions for under-prompt-bar chips (embeddings).
   * Caller session-filters (unasked only). Empty → no chips. There is no
   * canned fallback: the design no longer carries suggestion strings, because
   * a model guessing questions from a screenshot shipped generic filler next
   * to real, indexed ones.
   */
  pageQuestions?: string[];
  /**
   * Page sections for mid-page conversion lines (cold: section summary + chips).
   * Fleet-wide — headings/questions from page-voice only.
   */
  sections?: PlacementSectionPool[];
  /**
   * cold = section / page summary on conversion lines.
   * engaged = conversation continuity lead on every conversion line.
   */
  continuityMode?: "cold" | "engaged";
  /** When true, hero_bar shows an AI Voice control above the prompt. */
  voiceAssist?: boolean;
  /** Classic AI Voice mode — original text control + invite row render. */
  classicVoice?: boolean;
  /** When true, hero AI Voice control is green / On. */
  voiceSessionOn?: boolean;
};

/** Open chat only, or open + submit a full question (living-bar / typewriter click). */
export type PlacementActivate = {
  open: () => void;
  /** Submit the full displayed question (not partial mid-type text). */
  submitLine?: (text: string) => void;
  /**
   * CTA "Get the answers now" — open panel and seed suggested Qs (next section).
   */
  openWithQuestions?: (questions: string[]) => void;
  /** Toggle AI Voice session (hero AI Voice control). */
  toggleVoice?: () => void;
};

export function renderPlacements(
  customerId: string,
  design: WidgetRuntimeDesign,
  onActivate: (() => void) | PlacementActivate,
  opts: PlacementRenderOptions = {},
): void {
  ensurePlacementChrome();
  const activate: PlacementActivate =
    typeof onActivate === "function" ? { open: onActivate } : onActivate;
  const engaged = opts.continuityMode === "engaged";
  const globalLead =
    (opts.heroSummaryLines?.length ?? 0) > 0
      ? opts.heroSummaryLines!.map((s) => s.replace(/\s+/g, " ").trim()).filter(Boolean).join(" ")
      : "";
  let lineIndex = 0;

  (Object.keys(SLOT) as PlacementKind[]).forEach((kind) => {
    // Fill EVERY anchor of this kind (not just the first). Marketing pages often
    // mount multiple bhai-<id>-line divs; querySelector left the rest empty.
    const anchors = document.querySelectorAll<HTMLElement>(
      `div.bhai-${CSS.escape(customerId)}-${SLOT[kind]}`,
    );
    if (anchors.length === 0) return;
    const placement = design.placements[kind];
    for (const anchor of anchors) {
      anchor.replaceChildren();
      if (!placement.enabled) continue;
      const showHeadline = anchor.dataset.bhaiHeadline !== "off";
      const inPage = kind !== "nav_launcher";
      const monologueOn =
        inPage &&
        anchor.dataset.bhaiHeroMonologue === "on" &&
        (opts.heroMonologue?.length ?? 0) > 0;

      // Conversion lines: every in-page bar, whichever of its three div names
      // it was placed with, unless the host opts out with data-bhai-summary.
      // Section-aware cold, conversation-aware when engaged.
      const conversionLine = inPage && anchor.dataset.bhaiSummary !== "off";
      /** The soft closing band: the `-cta` name's look by default (live
       *  end-of-page divs keep it), available on any in-page bar. */
      const band =
        inPage &&
        (anchor.dataset.bhaiBand === "on" ||
          (kind === "end_of_page_banner" && anchor.dataset.bhaiBand !== "off"));

      const local = conversionLine
        ? resolveLineLocalContext(anchor, opts.sections ?? [])
        : { heading: "", questions: [] as string[] };

      let summaryText = "";
      let chips = opts.pageQuestions ?? [];
      let kicker = "AI · this page";

      if (conversionLine) {
        if (engaged && globalLead) {
          // Phase 2: conversation continuity on every conversion mount.
          summaryText = globalLead;
          chips =
            local.questions.length > 0
              ? local.questions
              : (opts.pageQuestions ?? []);
          kicker = "AI · continue";
        } else if (
          local.heading &&
          !isJunkSectionHeading(local.heading) &&
          local.questions.length > 0
        ) {
          // Phase 1 cold: section summary + section pills near this mount.
          const nice = titleishHeading(local.heading) || local.heading;
          summaryText = `Ask me about ${nice}.`;
          chips = local.questions;
          kicker = `AI · ${clipKicker(nice)}`;
        } else if (globalLead) {
          summaryText = globalLead;
          chips = opts.pageQuestions ?? [];
          kicker = lineIndex === 0 ? "AI · this page" : "AI · this section";
        } else if (monologueOn && opts.heroMonologue?.length) {
          summaryText = opts.heroMonologue
            .map((s) => s.replace(/\s+/g, " ").trim())
            .filter(Boolean)
            .join(" ");
        }
        lineIndex += 1;
      }

      const summaryLines = summaryText ? [summaryText] : undefined;
      const chipPool =
        chips.length > 0 ? chips : (opts.pageQuestions ?? undefined);

      anchor.appendChild(
        buildLauncher(
          kind,
          placement,
          design.theme,
          activate,
          showHeadline,
          summaryLines,
          conversionLine ? opts : undefined,
          chipPool,
          // Voice invite on every conversion stack.
          opts.voiceAssist === true && conversionLine,
          opts.voiceSessionOn === true,
          conversionLine,
          kicker,
          opts.classicVoice === true,
          band,
        ),
      );
    }
  });
}

/** Short kicker label from a section heading (fleet-wide). */
function clipKicker(heading: string): string {
  const t = heading.replace(/\s+/g, " ").trim();
  if (t.length <= 22) return t;
  return `${t.slice(0, 20).replace(/\s+\S*$/, "")}…`;
}

/**
 * Find section context for an in-content mount (nearest data-bhai-section or
 * matching page-voice section by proximity). Fleet-wide DOM heuristics only.
 */
function resolveLineLocalContext(
  anchor: HTMLElement,
  sections: readonly PlacementSectionPool[],
): { heading: string; questions: string[] } {
  // 1) Explicit host marker on self or ancestors
  let el: HTMLElement | null = anchor;
  for (let i = 0; i < 8 && el; i++) {
    const key = el.dataset?.bhaiSection?.replace(/\s+/g, " ").trim();
    if (key) {
      const match = matchSection(key, sections);
      if (match) return match;
    }
    el = el.parentElement;
  }

  // 2) Nearest previous [data-bhai-section] or h2 in document order
  const markers = document.querySelectorAll<HTMLElement>(
    "[data-bhai-section], h2, h3",
  );
  let best: HTMLElement | null = null;
  for (const m of markers) {
    if (m === anchor) break;
    // compareDocumentPosition: m precedes anchor
    if (
      m.compareDocumentPosition(anchor) & Node.DOCUMENT_POSITION_FOLLOWING
    ) {
      best = m;
    }
  }
  if (best) {
    const key =
      best.dataset?.bhaiSection?.replace(/\s+/g, " ").trim() ||
      (best.textContent ?? "").replace(/\s+/g, " ").trim();
    if (key) {
      const match = matchSection(key, sections);
      if (match) return match;
      // No page-voice match — still use heading for cold invite, page chips later
      if (key.length >= 3 && key.length <= 60) {
        return { heading: key, questions: [] };
      }
    }
  }

  return { heading: "", questions: [] };
}

function matchSection(
  key: string,
  sections: readonly PlacementSectionPool[],
): { heading: string; questions: string[] } | null {
  const n = key.toLowerCase();
  for (const s of sections) {
    const h = s.heading.replace(/\s+/g, " ").trim();
    const hn = h.toLowerCase();
    if (!hn) continue;
    if (hn === n || hn.includes(n) || n.includes(hn)) {
      return {
        heading: h,
        questions: s.questions
          .map((q) => q.replace(/\s+/g, " ").trim())
          .filter(Boolean)
          .slice(0, MAX_PAGE_PILLS + 2),
      };
    }
  }
  return null;
}

/** Classic AI Voice UI (original text controls + invite) vs tap-to-talk mic. */
let classicVoiceUi = false;

/** Mic glyph for string-rendered voice buttons — the tap-to-talk icon. */
const MIC_SVG =
  `<svg viewBox="0 0 24 24" width="15" height="15" fill="none" aria-hidden="true">` +
  `<rect x="9" y="3" width="6" height="11" rx="3" stroke="currentColor" stroke-width="2"/>` +
  `<path d="M5.5 11.5a6.5 6.5 0 0 0 13 0" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>` +
  `<path d="M12 18v3" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>` +
  `</svg>`;

/**
 * Sync all hero (and any) voice-mic buttons to session state without
 * re-rendering full placements. Call when voice turns on/off.
 */
export function setAiVoiceSessionUi(on: boolean): void {
  if (typeof document === "undefined") return;
  const label = on ? "AI Voice On" : "AI Voice";
  const aria = classicVoiceUi
    ? on
      ? "AI Voice is on. Tap to turn off"
      : "Turn AI Voice on"
    : on
      ? "Voice is on. Tap to turn off"
      : "Ask by voice";
  const title = classicVoiceUi
    ? on
      ? "AI Voice is on — tap to turn off"
      : "Turn AI Voice on"
    : on
      ? "Voice is on — tap to turn off"
      : "Ask by voice — tap, then speak";
  document.querySelectorAll<HTMLElement>("[data-bhai-voice='toggle']").forEach((btn) => {
    btn.classList.toggle("bhai-hero-voice-talk--on", on);
    btn.classList.toggle("bhai-voice-talk--session", on);
    btn.setAttribute("aria-pressed", on ? "true" : "false");
    btn.setAttribute("aria-label", aria);
    btn.title = title;
    if (classicVoiceUi) btn.textContent = label;
    else btn.innerHTML = MIC_SVG;
  });
}

// Theme colors as CSS variables (with literal fallbacks for first paint / no
// design). Nothing here is the raw model value — that's set safely by applyTheme.
const PRIMARY = "var(--bhjs-background,#2563eb)";
const ON_PRIMARY = "var(--bhjs-color,#ffffff)";
const HOVER = `var(--bhjs-background-hover,${PRIMARY})`;
/** Alpha tint of primary (replaces the old `${primary}XX` hex-alpha suffixes). */
const tint = (pct: number) => `color-mix(in srgb, ${PRIMARY} ${pct}%, transparent)`;

/**
 * Shared width for ALL in-page prompt bars. Always fill host mount up to cap —
 * never expand wider than the design frame (breaks glass hero layouts).
 */
const BAR_MAX = "var(--bhjs-bar-width, min(920px, 100%))";

// Theme surface for bars / chips (LLM palette via applyTheme). Fallbacks keep
// first paint readable when no design is loaded yet.
const SURFACE = "var(--bhjs-surface,#ffffff)";
const ON_SURFACE = "var(--bhjs-on-surface,#1f2937)";

/** Typewriter handle stashed on .bhai-tw-target by snippet.applyPlacementTypewriters. */
export type TwTarget = HTMLElement & {
  __bhaiTw?: { currentFullLine: () => { text: string } | null };
};

// Global light-DOM chrome for all in-page bars. Injected once (self-healing on
// SPA re-adopt). Unlayered so it wins over the theme @layer; host designers can
// still override with more specific unlayered rules if they must.
let chromeSheet: CSSStyleSheet | null = null;
let chromeStyleTag: HTMLStyleElement | null = null;

const PLACEMENT_CHROME_CSS = `
.bhai-launcher{
  box-sizing:border-box;
  width:100%;
  min-width:0;
  max-width:100%;
  font-family:system-ui,-apple-system,"Segoe UI",sans-serif;
  cursor:pointer;
}
.bhai-prompt-frame{
  box-sizing:border-box;
  width:100%;
  min-width:0;
  max-width:min(100%, ${BAR_MAX});
  margin:0 auto;
}
/* Fixed hero / conversion search stack — layout code-owned; theme via --bhjs-* */
.bhai-launcher--primary{
  width:100%;
  max-width:100%;
  margin:6px 0;
}
.bhai-ai-search{
  display:flex;
  flex-direction:column;
  gap:10px;
  width:100%;
  max-width:100%;
  box-sizing:border-box;
  /* Reserve vertical room: kicker + bar + chips + voice invite */
  min-height:0;
}
.bhai-ai-search__meta{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:8px;
  min-height:20px;
  padding:0 2px;
  box-sizing:border-box;
}
.bhai-ai-search__kicker{
  display:inline-flex;
  align-items:center;
  gap:6px;
  font-size:11px;
  font-weight:750;
  letter-spacing:0.04em;
  text-transform:uppercase;
  color:color-mix(in srgb, ${ON_SURFACE} 72%, ${PRIMARY});
}
.bhai-ai-search__kicker::before{
  content:"";
  width:6px;
  height:6px;
  border-radius:50%;
  background:linear-gradient(135deg,${PRIMARY},${HOVER});
  box-shadow:0 0 0 3px ${tint(18)};
  flex:none;
}
/* Dark hero backgrounds (optional class if host sets data-bhai-on-dark) */
.bhai-launcher--on-dark .bhai-ai-search__kicker{
  color:rgba(255,255,255,0.9);
  text-shadow:0 1px 2px rgba(15,23,42,0.35);
}
.bhai-prompt-bar{
  display:flex;
  align-items:center;
  gap:12px;
  box-sizing:border-box;
  width:100%;
  min-width:0;
  max-width:100%;
  height:58px;
  min-height:58px;
  max-height:58px;
  background:color-mix(in srgb, ${SURFACE} 96%, ${PRIMARY} 4%);
  border:1px solid ${tint(18)};
  border-radius:var(--bhai-prompt-radius,16px);
  padding:10px 12px 10px 14px;
  box-shadow:
    0 0 0 1px color-mix(in srgb, ${SURFACE} 70%, transparent) inset,
    0 8px 28px var(--bhjs-shadow, ${tint(12)}),
    0 2px 8px rgba(15,23,42,0.06);
  backdrop-filter:blur(10px);
  -webkit-backdrop-filter:blur(10px);
  transition:box-shadow .18s ease,border-color .18s ease;
  color:color-mix(in srgb, ${ON_SURFACE} 78%, #64748b);
}
/* New hero stack bar: taller so 2–3 line summary + CTA fit cleanly */
.bhai-launcher--primary .bhai-prompt-bar{
  height:auto;
  min-height:72px;
  max-height:none;
  border-radius:18px;
  padding:12px 14px;
  align-items:center;
}
.bhai-prompt-bar:hover{
  border-color:${tint(32)};
  box-shadow:
    0 0 0 1px color-mix(in srgb, ${SURFACE} 70%, transparent) inset,
    0 12px 36px ${tint(16)},
    0 0 0 3px ${tint(8)};
}
/* Summary / invite text inside the bar — room for 2–3 lines */
.bhai-prompt-summary{
  flex:1 1 0%;
  min-width:0;
  max-width:100%;
  color:${ON_SURFACE};
  font-size:14px;
  font-weight:600;
  letter-spacing:-0.01em;
  line-height:1.38;
  white-space:normal;
  overflow-wrap:anywhere;
  word-break:break-word;
  display:-webkit-box;
  -webkit-box-orient:vertical;
  -webkit-line-clamp:3;
  line-clamp:3;
  overflow:hidden;
  max-height:calc(1.38em * 3);
  padding-top:0;
  box-sizing:border-box;
  text-align:left;
}
.bhai-prompt-bar__icon{
  flex:none;
  display:flex;
  width:34px;
  height:34px;
  margin-top:0;
  align-items:center;
  justify-content:center;
  border-radius:9999px;
  background:linear-gradient(135deg,${tint(14)},${tint(8)});
  color:${PRIMARY};
  font-size:17px;
  box-shadow:0 0 0 3px ${tint(6)};
}
.bhai-prompt-bar__cta{
  flex:none;
  margin-top:0;
}
/* The bar's call to action. Geometry follows the tenant's corner style so
   Corners is not a lie on this element either. */
.bhai-cta-pill{
  flex:none;
  display:inline-flex;
  align-items:center;
  gap:6px;
  background:${PRIMARY};
  color:${ON_PRIMARY};
  border-radius:var(--bhjs-radius,16px);
  padding:10px 18px;
  font-size:15px;
  font-weight:700;
  white-space:nowrap;
}
.bhai-prompt-bar__cta .bhai-cta-pill{
  background:linear-gradient(135deg,${PRIMARY},color-mix(in srgb,${PRIMARY} 80%,#1e3a8a));
  box-shadow:0 4px 14px ${tint(28)};
  border-radius:var(--bhjs-radius,12px);
  padding:10px 16px;
  font-size:14px;
}
/* Button placement (nav_launcher) pill — always fully round; it is a pill by definition. */
.bhai-nav-pill{
  display:inline-flex;
  align-items:center;
  gap:6px;
  background:${PRIMARY};
  color:${ON_PRIMARY};
  border-radius:9999px;
  padding:8px 16px;
  font-size:14px;
  font-weight:700;
}
/* Closing band behind the end-of-page prompt bar. */
.bhai-cta-band{
  box-sizing:border-box;
  width:100%;
  min-width:0;
  max-width:100%;
  background:linear-gradient(180deg,${tint(4)},${tint(8)});
  border-radius:var(--bhjs-radius,16px);
  padding:30px 22px;
}
/* Typewriter target (non-summary bars only) — fixed 2-line box */
.bhai-tw-target{
  flex:1 1 0%;
  min-width:0;
  max-width:100%;
  color:#64748b;
  font-size:15px;
  font-weight:600;
  line-height:1.3;
  white-space:normal;
  overflow-wrap:anywhere;
  word-break:break-word;
  display:-webkit-box;
  -webkit-box-orient:vertical;
  -webkit-line-clamp:2;
  line-clamp:2;
  overflow:hidden;
  height:calc(1.3em * 2);
  min-height:calc(1.3em * 2);
  max-height:calc(1.3em * 2);
  padding-top:0;
  box-sizing:border-box;
}
/* Hero AI Voice — only on true hero_bar placement */
.bhai-hero-voice-row{
  display:flex;
  justify-content:flex-end;
  align-items:center;
  width:100%;
  box-sizing:border-box;
  margin:0;
  padding:0;
}
.bhai-hero-voice-talk{
  flex:none;
  border:1px solid ${tint(40)};
  background:${SURFACE};
  color:${PRIMARY};
  font-size:12px;
  font-weight:750;
  font-family:inherit;
  padding:6px 12px;
  border-radius:999px;
  cursor:pointer;
  white-space:nowrap;
  box-shadow:0 4px 16px rgba(15,23,42,.12);
}
.bhai-hero-voice-talk--mic{
  display:inline-grid;
  place-items:center;
  width:34px;
  height:34px;
  padding:0;
  border-radius:50%;
}
.bhai-hero-voice-talk:hover{
  background:color-mix(in srgb, ${SURFACE} 88%, ${PRIMARY} 12%);
  border-color:${tint(55)};
}
.bhai-hero-voice-talk--on{
  background:linear-gradient(135deg,#22c55e,#16a34a);
  color:#fff;
  border-color:transparent;
  box-shadow:0 0 0 3px rgba(22,163,74,.28);
}
/* Voice invite row — reserved under chips (fixed template slot) */
.bhai-placement-voice-invite{
  display:flex;
  flex-wrap:wrap;
  align-items:center;
  justify-content:flex-start;
  gap:6px 8px;
  width:100%;
  box-sizing:border-box;
  margin:0;
  min-height:40px;
  padding:8px 12px;
  border-radius:12px;
  background:color-mix(in srgb, ${SURFACE} 88%, ${PRIMARY} 12%);
  border:1px solid ${tint(18)};
  font-family:inherit;
  font-size:12.5px;
  font-weight:600;
  line-height:1.35;
  color:${ON_SURFACE};
  text-align:left;
}
.bhai-placement-voice-invite__text{
  flex:none;
  color:color-mix(in srgb, ${ON_SURFACE} 85%, ${PRIMARY});
  font-weight:600;
}
.bhai-placement-voice-invite .bhai-hero-voice-talk{
  box-shadow:0 2px 10px rgba(15,23,42,.1);
}
/* Chips row — always has vertical room under the bar */
.bhai-placement-chips--centered{
  display:flex;
  flex-wrap:wrap;
  gap:8px;
  margin-top:10px;
  justify-content:center;
}
.bhai-launcher--primary .bhai-placement-chips,
.bhai-ai-search .bhai-placement-chips{
  display:flex;
  flex-wrap:wrap;
  gap:8px;
  margin-top:0;
  min-height:36px;
  justify-content:flex-start;
  width:100%;
  max-width:100%;
  box-sizing:border-box;
}
/* Chip base — every chip, on every bar. Previously each chip carried this as
   an inline style and the rules below had to shout it down with !important. */
.bhai-placement-chip{
  background:${SURFACE};
  border:1px solid ${tint(25)};
  color:${HOVER};
  border-radius:999px;
  padding:7px 13px;
  font-size:13px;
  font-weight:600;
  cursor:pointer;
  font-family:inherit;
  line-height:1.3;
  text-align:left;
  max-width:100%;
}
.bhai-launcher--primary .bhai-placement-chip,
.bhai-ai-search .bhai-placement-chip{
  border-radius:999px;
  padding:8px 13px;
  font-size:12.5px;
  font-weight:600;
  line-height:1.25;
  background:color-mix(in srgb, ${SURFACE} 94%, ${PRIMARY} 6%);
  border:1px solid ${tint(22)};
  color:${PRIMARY};
  box-shadow:0 2px 8px rgba(15,23,42,0.06);
  transition:transform .12s ease,box-shadow .12s ease,border-color .12s ease;
  max-width:100%;
}
.bhai-launcher--primary .bhai-placement-chip:hover,
.bhai-ai-search .bhai-placement-chip:hover{
  transform:translateY(-1px);
  box-shadow:0 6px 14px ${tint(14)};
  border-color:${PRIMARY};
  /* Resting state is PRIMARY text on a 6%-PRIMARY tint — about 4.5:1, the
     bare AA floor, and hover moved only the shadow and border, so the chip
     barely acknowledged the pointer. Hover now INVERTS to the solid brand
     colour with its own on-colour text: unmistakably interactive, and the
     contrast goes up rather than washing further out. */
  background:${PRIMARY};
  color:${ON_PRIMARY};
}
.bhai-launcher--primary .bhai-placement-chip:focus-visible,
.bhai-ai-search .bhai-placement-chip:focus-visible{
  outline:2px solid ${PRIMARY};
  outline-offset:2px;
}
.bhai-launcher--primary .bhai-placement-chip:active,
.bhai-ai-search .bhai-placement-chip:active{
  transform:translateY(0);
}
@media (max-width:640px){
  .bhai-launcher--primary .bhai-prompt-bar{
    min-height:72px;
    padding:12px 14px;
  }
  .bhai-prompt-summary{
    font-size:14px;
    -webkit-line-clamp:3;
    line-clamp:3;
    max-height:calc(1.38em * 3);
  }
  .bhai-launcher--primary .bhai-placement-chip,
  .bhai-ai-search .bhai-placement-chip{
    padding:10px 14px;
    font-size:13.5px;
    line-height:1.3;
  }
  .bhai-placement-voice-invite{
    min-height:40px;
    padding:9px 12px;
    font-size:13px;
  }
}
`;

/**
 * The whole chrome sheet, wrapped in a cascade layer.
 *
 * Un-layered author CSS beats ANY layered rule, whatever its specificity — so
 * with this in place a site designer overrides our bar with a plain
 * `.bhai-launcher .bhai-placement-chip { … }`, no `!important`, no specificity
 * arithmetic. It only works because the `!important` declarations are gone:
 * an important declaration inside a layer OUTRANKS an un-layered important
 * one, so layering while keeping them would have made the page LESS
 * overridable, not more.
 */
function layeredChromeCss(): string {
  return `@layer bhai-widget {\n${PLACEMENT_CHROME_CSS}\n}`;
}

/** Ensure global prompt-bar layout CSS is present (idempotent; re-syncs CSS). */
export function ensurePlacementChrome(): void {
  try {
    if ("adoptedStyleSheets" in Document.prototype && typeof CSSStyleSheet === "function") {
      if (!chromeSheet) {
        chromeSheet = new CSSStyleSheet();
      }
      // Always re-apply so height/clamp fixes land on SPA re-render / HMR.
      chromeSheet.replaceSync(layeredChromeCss());
      if (!document.adoptedStyleSheets.includes(chromeSheet)) {
        document.adoptedStyleSheets = [...document.adoptedStyleSheets, chromeSheet];
      }
      return;
    }
  } catch {
    /* fall through to <style> tag */
  }
  if (!chromeStyleTag || !chromeStyleTag.isConnected) {
    chromeStyleTag = document.createElement("style");
    chromeStyleTag.setAttribute("data-bhai-placement-chrome", "1");
    (document.head || document.documentElement).appendChild(chromeStyleTag);
  }
  chromeStyleTag.textContent = layeredChromeCss();
}

function buildLauncher(
  kind: PlacementKind,
  p: PlacementRuntime,
  theme: Theme,
  activate: PlacementActivate,
  // Headline visibility moved into skin CSS (living-bar polish); param kept
  // (underscored) so positional call sites stay stable.
  _showHeadline = true,
  heroMonologue?: string[],
  monologueOpts?: PlacementRenderOptions,
  /** Corpus embeddings for under-bar chips (already session-filtered). */
  pageQuestions?: string[],
  /** Show AI Voice control above hero prompt (dashboard voiceAssist). */
  voiceAssist = false,
  /** When true, hero control shows green AI Voice On state. */
  voiceSessionOn = false,
  /**
   * Conversion search stack: fixed bar + kicker + chips (top hero + mid-page
   * in-content lines). Never typewriter page-question teasers on these.
   */
  isTopLine = false,
  /** Kicker label for conversion stack (e.g. "AI · this page"). */
  kickerLabel = "AI · this page",
  /** Classic AI Voice UI (original text controls + invite row). */
  classicVoice = false,
  /** In-page bar on the soft closing band (the `-cta` look). */
  band = false,
): HTMLElement {
  ensurePlacementChrome();

  // Enum-derived values (8px/16px/9999px, and a shadow rgba), never raw model
  // strings — safe to interpolate. One shared rule with the dashboard preview
  // so the picture a tenant approves is the bar they get.
  const radius = barRadius(theme);
  const c = p.copy;

  // Classic under-bar chips only — never section dual-chrome on in-page
  // placements. Page-voice is the ONLY source: during a reindex window the bar
  // shows no chips, which is better than showing invented ones.
  let chipQs = (pageQuestions ?? [])
    .map((q) => q.replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .slice(0, MAX_PAGE_PILLS + 2);

  const el = document.createElement("div");
  el.className = `bhai-launcher bhai-launcher--${kind}`;
  el.style.setProperty("--bhai-prompt-radius", radius);

  // Class, not an inline style: inline styles can only be beaten with
  // !important, which is what forced the !important arms race this chrome
  // used to carry — and which locked host designers out along with it.
  const btn = (label: string) =>
    `<span class="bhai-cta-pill">${esc(label)} &#10148;</span>`;

  const chips = (items: string[], compact = false) =>
    items.length
      ? `<div class="bhai-placement-chips${compact ? "" : " bhai-placement-chips--centered"}">${items
          .map(
            (s) =>
              `<button type="button" class="bhai-placement-chip" data-bhai-q="${esc(s)}">${esc(pillLabel(s))}</button>`,
          )
          .join("")}</div>`
      : "";

  // Prompt bar: static page summary (no typewriter) when summary is set;
  // otherwise classic typewriter target for mid-page / end bars.
  const promptBar = (placeholder: string, staticSummary?: string) => {
    const body = staticSummary
      ? `<span class="bhai-prompt-summary" role="status" title="${esc(staticSummary)}">${esc(staticSummary)}</span>`
      : `<span class="bhai-tw-target">${esc(placeholder)}</span>`;
    return (
      `<div class="bhai-prompt-bar">` +
      `<span class="bhai-prompt-bar__icon" aria-hidden="true">&#10022;</span>` +
      body +
      `<span class="bhai-prompt-bar__cta">${btn(c.cta || "Ask")}</span></div>`
    );
  };

  const frame = (inner: string) =>
    `<div class="bhai-prompt-frame">${inner}</div>`;

  if (kind !== "nav_launcher") {
    // The in-page bar — the same for all three div names. The fixed AI search
    // stack (kicker + bar + chips + voice room) unless the host opted out of
    // the summary; layout/size are code-owned, the LLM only themes colors.
    const ph = askAboutCopy(c.placeholder || "Ask me anything");
    const useSearchStack = isTopLine;
    // Faithful page summary lives INSIDE the fixed prompt bar (multi-line clamp).
    let summaryText =
      heroMonologue && heroMonologue.length > 0
        ? heroMonologue
            .map((s) => s.replace(/\s+/g, " ").trim())
            .filter(Boolean)
            .join(" ")
        : "";
    summaryText = dedupeSummaryFromChips(summaryText, chipQs) || "";
    if (useSearchStack) {
      el.classList.add("bhai-launcher--primary");
    }
    chipQs = filterChipsAgainstSummary(chipQs, summaryText).slice(
      0,
      MAX_PAGE_PILLS,
    );
    const heroVoiceOn = voiceSessionOn;
    classicVoiceUi = classicVoice === true;
    // Voice control: search-stack mounts when voiceAssist.
    const showVoice = voiceAssist && useSearchStack;
    const heroVoiceBtn = showVoice
      ? classicVoiceUi
        ? `<button type="button" class="bhai-hero-voice-talk${heroVoiceOn ? " bhai-hero-voice-talk--on" : ""}" data-bhai-voice="toggle" aria-pressed="${heroVoiceOn ? "true" : "false"}" aria-label="${heroVoiceOn ? "AI Voice is on. Tap to turn off" : "Turn AI Voice on"}" title="${heroVoiceOn ? "AI Voice is on — tap to turn off" : "Turn AI Voice on"}">${heroVoiceOn ? "AI Voice On" : "AI Voice"}</button>`
        : `<button type="button" class="bhai-hero-voice-talk bhai-hero-voice-talk--mic${heroVoiceOn ? " bhai-hero-voice-talk--on" : ""}" data-bhai-voice="toggle" aria-pressed="${heroVoiceOn ? "true" : "false"}" aria-label="${heroVoiceOn ? "Voice is on. Tap to turn off" : "Ask by voice"}" title="${heroVoiceOn ? "Voice is on — tap to turn off" : "Ask by voice — tap, then speak"}">${MIC_SVG}</button>`
      : "";
    // Classic AI Voice keeps its original invite row; tap-to-talk retires it
    // (the mic sits in the kicker row instead).
    const voiceInvite =
      classicVoiceUi && showVoice && !voiceSessionOn && heroVoiceBtn
        ? `<div class="bhai-placement-voice-invite" data-bhai-voice-invite="1">` +
          `<span class="bhai-placement-voice-invite__text">I can talk too —</span>` +
          heroVoiceBtn +
          `<span class="bhai-placement-voice-invite__text">let’s chat</span>` +
          `</div>`
        : "";

    const barBody = useSearchStack
      ? summaryText || ph
      : summaryText || undefined;

    if (useSearchStack) {
      const kickerText = (kickerLabel || "AI · this page").replace(/\s+/g, " ").trim();
      const kicker = `<div class="bhai-ai-search__meta">` +
        `<span class="bhai-ai-search__kicker">${esc(kickerText)}</span>` +
        (heroVoiceBtn && !voiceInvite
          ? `<span class="bhai-hero-voice-row">${heroVoiceBtn}</span>`
          : "") +
        `</div>`;
      // Fixed stack: kicker → bar (summary) → chips → voice invite
      el.innerHTML = frame(
        `<div class="bhai-ai-search">${kicker}${promptBar(ph, barBody)}${chips(chipQs, true)}${voiceInvite}</div>`,
      );
    } else {
      // Rare: in_content with conversion stack opt-out — still chips + bar.
      // Classic carries the invite row; tap-to-talk gets the bare mic row.
      el.innerHTML = frame(
        `${promptBar(ph, barBody)}${chips(chipQs)}${
          voiceInvite ||
          (heroVoiceBtn ? `<span class="bhai-hero-voice-row">${heroVoiceBtn}</span>` : "")
        }`,
      );
    }
    // Wrapped BEFORE any listener binds — rewriting innerHTML after would drop them.
    if (band) el.innerHTML = `<div class="bhai-cta-band">${el.innerHTML}</div>`;
    if (summaryText) {
      monologueOpts?.onHeroMonologueShown?.();
    }
    el.querySelectorAll<HTMLElement>("[data-bhai-voice='toggle']").forEach((btn) => {
      btn.addEventListener("click", (ev) => {
        ev.stopPropagation();
        if (activate.toggleVoice) activate.toggleVoice();
        else activate.open();
      });
    });
  } else {
    el.innerHTML = `<span class="bhai-nav-pill">&#10022; ${esc(c.cta || "Ask")}</span>`;
  }

  // Chip tap = submit that corpus question. Bar body = open panel only (pick /
  // type); never auto-submit the rolling typewriter teaser.
  el.querySelectorAll<HTMLElement>(".bhai-placement-chip").forEach((chip) => {
    chip.addEventListener("click", (ev) => {
      ev.stopPropagation();
      const q = (chip.getAttribute("data-bhai-q") ?? "").trim();
      if (q && activate.submitLine) activate.submitLine(q);
      else activate.open();
    });
  });

  el.addEventListener("click", () => {
    activate.open();
  });
  return el;
}

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[ch] as string);
}

function normLead(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s$%]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * If the bar summary is the same as (or is) an inventory pill, clear it so we
 * don't show the same string in the bar and as the first chip.
 */
function dedupeSummaryFromChips(
  summary: string,
  chips: readonly string[],
): string {
  const s = summary.replace(/\s+/g, " ").trim();
  if (!s) return "";
  const sn = normLead(s);
  for (const c of chips) {
    const cn = normLead(c);
    if (!cn) continue;
    if (sn === cn || sn.includes(cn) || cn.includes(sn)) {
      // Summary is just a chip restatement — use invite fallback (caller).
      return "";
    }
  }
  // TTS-style junk never belongs in the bar
  if (/want to continue with:|related to .+\. want to/i.test(s)) return "";
  return s;
}

/** Remove chips that duplicate the on-bar summary line. */
function filterChipsAgainstSummary(
  chips: readonly string[],
  summary: string,
): string[] {
  const sn = normLead(summary);
  if (!sn) return [...chips];
  return chips.filter((c) => {
    const cn = normLead(c);
    if (!cn) return false;
    if (sn === cn) return false;
    // Near-dupe: chip fully contained in summary as the only ask
    if (sn.includes(cn) && cn.length >= 12) return false;
    return true;
  });
}
